import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DataTable, type DataTableGrouping } from '@odo/shared-ui';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FOCUS_TARGET_CLASS, useFocusCaseScroll } from './useFocusCaseScroll';
import {
    type WorkQueueCase,
    type StatusMeta,
    type OutgoingAction,
    type SortBy,
    type SortDir,
    type PriorityCounts
} from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { buildCaseColumns, type CaseMilestoneColumn } from './caseColumns';
import { TeamSplitPill } from '@odo/shared-ui/components/TeamPill';
import { possibleOwners } from './stepOwners';
import { extraColumnsFor } from './caseColumnStatuses';
import { PrioritySectionDivider } from './priority/PrioritySectionDivider';
import { type PriorityLevel } from './priority/priority';

// Marks real data rows so the viewport-fit measurement never picks up a
// skeleton/empty/divider row, whose different heights would oscillate pageSize.
const CASE_ROW_CLASS = 'wq-case-row';
const DATA_ROW_SELECTOR = `tr.${CASE_ROW_CLASS}`;

// Synthetic group keys for the DataTable grouping: the pinned search result
// sits alone above everything; REST covers rows outside priority sections.
const PINNED_GROUP = '__pinned';
const REST_GROUP = '__rest';

// Refinement steps from Ref-2.1 onward (records submitted for refinement).
// These steps show the REFI SUBMITTED milestone date column.
const REFI_SUBMITTED_MILESTONE_STATUSES: ReadonlySet<string> = new Set([
    'Ref - 2.1. Refinement records under review',
    'Ref - 2.2. Create refinement in manufacturer',
    'Ref - 3. Submit refinement for design',
    'Ref - 4. Waiting for Refinement design',
    'Ref - 4.1. Refinement design pre-approved',
    'Ref - 5. Review refinement design',
    'Ref - 6. Create RDI',
    'Ref - 6.1. Pending refinement approval',
    'Ref - 7. Submit for manufacturing',
    'Ref - 8. Waiting for manufacturing',
    'Ref - 9. Pending patient appointment update'
]);

type ViewMode = 'paged' | 'scroll';

// Returns true when the visible rows have more than just 'normal' priority,
// meaning dividers are meaningful and should be shown.
function hasMixedPriorities(rows: WorkQueueCase[]): boolean {
    return rows.some((c) => c.priority !== 'normal');
}

// All-sections-expanded shape, shared by the initial fold state, the
// focus-time reset, and the render-level focus guard.
const ALL_EXPANDED: Record<PriorityLevel, boolean> = {
    extreme: false,
    high: false,
    normal: false
};

// Persisted fold state for priority sections, mirroring other Work Queue view
// prefs: lazy read on mount, write-through on change, silent on failure.
const COLLAPSED_SECTIONS_KEY = 'odo:wq:priority-collapsed';

function loadCollapsedSections(): Record<PriorityLevel, boolean> {
    if (typeof globalThis === 'undefined' || !globalThis.localStorage) return ALL_EXPANDED;
    try {
        const raw = globalThis.localStorage.getItem(COLLAPSED_SECTIONS_KEY);
        if (!raw) return ALL_EXPANDED;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
            return ALL_EXPANDED;
        // Rebuild from known keys only - never trust the stored shape wholesale.
        const record = parsed as Record<string, unknown>;
        return {
            extreme: record.extreme === true,
            high: record.high === true,
            normal: record.normal === true
        };
    } catch {
        return ALL_EXPANDED;
    }
}

function persistCollapsedSections(next: Record<PriorityLevel, boolean>): void {
    try {
        globalThis.localStorage?.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
    } catch {
        /* ignore - persistence is best-effort */
    }
}

const SUBMITTED_MILESTONE: CaseMilestoneColumn = {
    label: 'SUBMITTED',
    sortKey: 'submitted',
    field: 'submittedAt'
};
const APPROVED_MILESTONE: CaseMilestoneColumn = {
    label: 'APPROVED',
    sortKey: 'approved',
    field: 'approvedAt'
};
const REFI_SUBMITTED_MILESTONE: CaseMilestoneColumn = {
    label: 'REFI SUBMITTED',
    sortKey: 'refi-submitted',
    field: 'refiSubmittedAt'
};

// Which milestone date a step shows: submission on design/redesign,
// submission+approval on manufacturing, refi submission from Ref-2.1 onward.
function milestoneColumnsFor(
    selectedMacro: string | undefined,
    selectedKey: string | null
): CaseMilestoneColumn[] {
    if (selectedMacro === 'design' || selectedMacro === 'redesign') return [SUBMITTED_MILESTONE];
    if (selectedMacro === 'manufacturing') return [SUBMITTED_MILESTONE, APPROVED_MILESTONE];
    if (selectedKey && REFI_SUBMITTED_MILESTONE_STATUSES.has(selectedKey))
        return [REFI_SUBMITTED_MILESTONE];
    return [];
}

interface CaseListProps {
    cases: WorkQueueCase[] | undefined;
    total: number;
    totalPages: number;
    currentPage: number;
    onPageChange: (next: number) => void;
    onPageSizeChange: (n: number) => void;
    viewMode: ViewMode;
    onViewModeChange: (next: ViewMode) => void;
    // Infinite-scroll plumbing (scroll mode only): hasMore reflects server
    // pages left, onLoadMore fires from DataTable's sentinel/button.
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    selectedKey: string | null;
    selectedCaseId: string | null;
    onSelectCase: (id: string | null) => void;
    onAction: (c: WorkQueueCase, outgoingAction: OutgoingAction) => void;
    onStartTask: (id: string) => void;
    statusLookup: Map<string, StatusMeta>;
    isLoading: boolean;
    sortBy: SortBy;
    dir: SortDir;
    onSortChange: (next: SortBy) => void;
    onRefresh: () => void;
    isRefreshing: boolean;
    // Set by WorkQueue after a search-dropdown selection to auto-scroll to that
    // row; cleared via onFocusHandled. Must NOT be set on ordinary row clicks.
    focusCaseId?: string | null;
    onFocusHandled?: () => void;
    /** Case pinned above regular rows when the selected case (?case=) isn't
     * in the loaded rows; deduped if it also appears in `cases`. */
    pinnedCase?: WorkQueueCase | null;
    /** When true, renders labeled priority section dividers between case rows. */
    splitByPriority?: boolean;
    /** Per-level case counts for the current step + filters, returned by the
     * server when splitByPriority is requested; optional for version-skew safety. */
    priorityCounts?: PriorityCounts;
}

export function CaseList({
    cases,
    total,
    totalPages,
    currentPage,
    onPageChange,
    onPageSizeChange,
    viewMode,
    onViewModeChange,
    hasMore,
    isLoadingMore,
    onLoadMore,
    selectedKey,
    selectedCaseId,
    onSelectCase,
    onAction,
    onStartTask,
    statusLookup,
    isLoading,
    sortBy,
    dir,
    onSortChange,
    onRefresh,
    isRefreshing,
    focusCaseId,
    onFocusHandled,
    pinnedCase,
    splitByPriority = false,
    priorityCounts
}: CaseListProps) {
    const catalog = useStatusCatalog();
    const {
        showAlignerColumn,
        showManufacturerColumn,
        showManufacturerStatusColumn,
        showRefinementsColumn,
        showVaCountsColumn
    } = extraColumnsFor(selectedKey);
    const selectedMacro = selectedKey ? statusLookup.get(selectedKey)?.macro : undefined;

    const milestones = useMemo(
        () => milestoneColumnsFor(selectedMacro, selectedKey),
        [selectedMacro, selectedKey]
    );

    const columns = useMemo(
        () =>
            buildCaseColumns({
                showAlignerColumn,
                showManufacturerColumn,
                showManufacturerStatusColumn,
                showRefinementsColumn,
                showVaCountsColumn,
                milestones,
                statusLookup,
                onAction,
                onStartTask
            }),
        [
            showAlignerColumn,
            showManufacturerColumn,
            showManufacturerStatusColumn,
            showRefinementsColumn,
            showVaCountsColumn,
            milestones,
            statusLookup,
            onAction,
            onStartTask
        ]
    );

    const [collapsedSections, setCollapsedSections] =
        useState<Record<PriorityLevel, boolean>>(loadCollapsedSections);

    // Guard: skip the initial-mount write so we don't redundantly persist the
    // value we just loaded from localStorage.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        persistCollapsedSections(collapsedSections);
    }, [collapsedSections]);

    function toggleSection(level: PriorityLevel) {
        setCollapsedSections((prev) => ({ ...prev, [level]: !prev[level] }));
    }

    // Render-level focus guard: effects run after commit, so an effect-only
    // expansion would be one commit late for useFocusCaseScroll's DOM check.
    const effectiveCollapsed = focusCaseId ? ALL_EXPANDED : collapsedSections;

    const containerRef = useRef<HTMLDivElement>(null);
    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const paginationRef = useRef<HTMLDivElement>(null);
    // Tracks the last pageSize pushed up to skip redundant writes - same-value
    // setState still re-renders in dev mode and chains through the query key.
    const lastPageSizeRef = useRef<number | null>(null);
    // Keep the latest props/callback so the measurement function (registered
    // once on mount) always reads fresh values without re-binding listeners.
    const onPageSizeChangeRef = useRef(onPageSizeChange);
    onPageSizeChangeRef.current = onPageSizeChange;
    // Same idea for viewMode - scroll mode locks the limit in the parent, so
    // any measurement push from here would fight that.
    const viewModeRef = useRef(viewMode);
    viewModeRef.current = viewMode;

    useLayoutEffect(() => {
        function measure() {
            // Scroll mode owns its own limit - skip the viewport-fit cascade.
            if (viewModeRef.current === 'scroll') return;
            if (!containerRef.current || !tableHeaderRef.current || !tableBodyRef.current) return;
            // Only ever measure a real data row - never a skeleton, empty or
            // divider row, whose different heights would oscillate pageSize.
            const firstRow = tableBodyRef.current.querySelector<HTMLElement>(DATA_ROW_SELECTOR);
            if (!firstRow) return;
            const containerTop = containerRef.current.getBoundingClientRect().top;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const headerHeight = tableHeaderRef.current.offsetHeight;
            const paginationHeight = paginationRef.current?.offsetHeight || 0;
            const rowHeight = firstRow.offsetHeight || 64;
            const availableHeight = Math.max(
                0,
                viewportHeight - containerTop - headerHeight - paginationHeight - 16
            );
            const next = Math.max(1, Math.floor(availableHeight / rowHeight));
            if (next === lastPageSizeRef.current) return;
            lastPageSizeRef.current = next;
            onPageSizeChangeRef.current(next);
        }

        // Initial measurement (may bail if rows aren't in the DOM yet).
        measure();

        // Re-measure when the tbody resizes - covers the "first data rows render"
        // case without listening to React state changes.
        const ro = new ResizeObserver(measure);
        if (tableBodyRef.current) ro.observe(tableBodyRef.current);

        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('resize', measure);
            ro.disconnect();
        };
    }, []);

    // One-shot scroll to the focused case once it's in the loaded rows;
    // pinnedCase covers the not-loaded case so no fetch loop is needed.
    const noopFocusHandledRef = useRef(() => {
        /* intentionally empty */
    });
    const safeFocusHandled = onFocusHandled ?? noopFocusHandledRef.current;
    useFocusCaseScroll({
        focusCaseId: focusCaseId ?? null,
        cases,
        tbodyRef: tableBodyRef,
        onFocusHandled: safeFocusHandled
    });

    // Also reset the STORED fold state on focus, or collapsed sections would
    // snap shut under the row the moment focusCaseId clears.
    useEffect(() => {
        if (!focusCaseId) return;
        setCollapsedSections(ALL_EXPANDED);
    }, [focusCaseId]);

    // Dedupe: the pinned case never renders twice even when it is also in the
    // loaded rows.
    const regularCases = useMemo(
        () => (pinnedCase ? (cases ?? []).filter((c) => c.id !== pinnedCase.id) : (cases ?? [])),
        [cases, pinnedCase]
    );
    const allRows = useMemo(
        () => (pinnedCase ? [pinnedCase, ...regularCases] : regularCases),
        [pinnedCase, regularCases]
    );

    const showPriorityDividers = splitByPriority && hasMixedPriorities(regularCases);

    // Folding a group pulls load-more into view without any user scroll and
    // would chain-fetch hidden rows - hide it while the list bottom is folded.
    const lastRow = allRows[allRows.length - 1];
    const bottomGroupCollapsed =
        showPriorityDividers && lastRow !== undefined && effectiveCollapsed[lastRow.priority];

    // Pinned row and priority sections both ride DataTable's grouping: pinned
    // leads with a separator under it; priorities become collapsible sections.
    const grouping: DataTableGrouping<WorkQueueCase> | undefined =
        pinnedCase || showPriorityDividers
            ? {
                  getGroupKey: (row) =>
                      pinnedCase && row.id === pinnedCase.id
                          ? PINNED_GROUP
                          : showPriorityDividers
                            ? row.priority
                            : REST_GROUP,
                  collapsed: showPriorityDividers ? effectiveCollapsed : undefined,
                  renderDivider: (groupKey, colSpan, prevGroupKey) => (
                      <>
                          {prevGroupKey === PINNED_GROUP && (
                              <tr aria-hidden="true">
                                  <td
                                      colSpan={colSpan}
                                      className="h-0 p-0 border-b-2 border-gray-200"
                                  />
                              </tr>
                          )}
                          {groupKey !== PINNED_GROUP && groupKey !== REST_GROUP && (
                              <PrioritySectionDivider
                                  level={groupKey as PriorityLevel}
                                  priorityCounts={priorityCounts}
                                  colSpan={colSpan}
                                  collapsed={effectiveCollapsed[groupKey as PriorityLevel]}
                                  onToggle={() => toggleSection(groupKey as PriorityLevel)}
                              />
                          )}
                      </>
                  )
              }
            : undefined;

    if (!selectedKey) {
        return (
            <div className="flex-1 rounded-xl border border-gray-200 bg-white flex flex-col items-center justify-center gap-2 text-gray-400 min-w-0">
                <span className="text-4xl">←</span>
                <span className="text-sm">Select a group to view cases</span>
            </div>
        );
    }

    const headerTitle = catalog.display(selectedKey);
    const stepMeta = statusLookup.get(selectedKey);

    // Card header: step name + count + team pill + refresh, inside the card.
    const cardHeader = (
        <div className="px-6 py-3 flex items-center gap-2 border-b border-gray-200 flex-shrink-0">
            <span className="font-inter font-semibold text-sm text-gray-800 truncate">
                {headerTitle}
            </span>
            <span className="font-inter text-xs text-gray-500">
                · {total.toLocaleString()} {total === 1 ? 'patient' : 'patients'}
            </span>
            {stepMeta && <TeamSplitPill teams={possibleOwners(stepMeta)} />}
            <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                aria-label="Refresh cases"
                title="Refresh cases"
                className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
                <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
        </div>
    );

    return (
        <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden min-w-0">
            <DataTable
                label={`Work queue cases: ${headerTitle}`}
                columns={columns}
                rows={allRows}
                getRowKey={(c) => c.id}
                cardHeader={cardHeader}
                grouping={grouping}
                sort={{ by: sortBy, order: dir, onSort: onSortChange }}
                mode={viewMode === 'scroll' ? 'infinite' : 'paged'}
                onModeChange={(m) => onViewModeChange(m === 'infinite' ? 'scroll' : 'paged')}
                pagination={
                    total > 0
                        ? { page: currentPage, totalPages, total, onPage: onPageChange }
                        : undefined
                }
                onLoadMore={onLoadMore}
                hasMore={hasMore && !bottomGroupCollapsed}
                isLoadingMore={isLoadingMore}
                rowAction={{
                    mode: 'click',
                    onActivate: (row) => onSelectCase(row.id === selectedCaseId ? null : row.id)
                }}
                rowClassName={(row) =>
                    [
                        CASE_ROW_CLASS,
                        'font-inter text-xs text-gray-700',
                        row.id === selectedCaseId ? '!bg-blue-50' : '',
                        focusCaseId && row.id === focusCaseId ? FOCUS_TARGET_CLASS : ''
                    ]
                        .filter(Boolean)
                        .join(' ')
                }
                isLoading={isLoading}
                emptyState={
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                        <span className="text-2xl">✓</span>
                        <span className="text-sm">Queue clear</span>
                    </div>
                }
                refs={{
                    head: tableHeaderRef,
                    body: tableBodyRef,
                    pagination: paginationRef
                }}
            />
        </div>
    );
}
